// client/src/components/admin/MarketsManagement.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Edit2, Trash2, Save, X, TrendingUp } from 'lucide-react';
import '../../styles/MarketsManagement.css';

const EuriborManagement = () => {
  const { i18n } = useTranslation();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    date: '',
    euribor1W: '',
    euribor1M: '',
    euribor3M: '',
    euribor6M: '',
    euribor12M: '',
    estrON: '',
    soniaON: '',
    sofrON: '',
    aoniaON: ''
  });

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/markets/rates');
      setRates(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching rates:', err);
      setError('Error loading data');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      date: '',
      euribor1W: '',
      euribor1M: '',
      euribor3M: '',
      euribor6M: '',
      euribor12M: '',
      estrON: '',
      soniaON: '',
      sofrON: '',
      aoniaON: ''
    });
    setIsAdding(false);
    setEditingId(null);
    setError('');
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await axios.post('/api/markets/rates', formData);
      setSuccess('Rate added successfully');
      resetForm();
      fetchRates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error adding rate:', err);
      setError(err.response?.data?.message || 'Error adding rate');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await axios.put(`/api/markets/rates/${editingId}`, formData);
      setSuccess('Rate updated successfully');
      resetForm();
      fetchRates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating rate:', err);
      setError(err.response?.data?.message || 'Error updating rate');
    }
  };

  const handleEdit = (rate) => {
    setFormData({
      date: new Date(rate.date).toISOString().split('T')[0],
      euribor1W: rate.euribor1W,
      euribor1M: rate.euribor1M,
      euribor3M: rate.euribor3M,
      euribor6M: rate.euribor6M,
      euribor12M: rate.euribor12M,
      estrON: rate.estrON ?? '',
      soniaON: rate.soniaON ?? '',
      sofrON: rate.sofrON ?? '',
      aoniaON: rate.aoniaON ?? ''
    });
    setEditingId(rate._id);
    setIsAdding(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this rate?')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await axios.delete(`/api/markets/rates/${id}`);
      setSuccess('Rate deleted successfully');
      fetchRates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting rate:', err);
      setError('Error deleting rate');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'it' ? 'it-IT' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="euribor-management">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="euribor-management">
      <div className="euribor-header">
        <div className="header-content">
          <TrendingUp size={32} />
          <div>
            <h1>Euribor Rates Management</h1>
            <p>Manage daily Euribor rates for the Markets page</p>
          </div>
        </div>
        <button 
  className="btn-add"
  onClick={() => {
    setError('');
    setSuccess('');
    setEditingId(null);
    setFormData({
      date: '',
      euribor1W: '',
      euribor1M: '',
      euribor3M: '',
      euribor6M: '',
      euribor12M: '',
      estrON: '',
      soniaON: '',
      sofrON: '',
      aoniaON: ''
    });
    setIsAdding(true);
  }}
  disabled={isAdding || editingId}
>
          <Plus size={20} />
          Add New Rate
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      {/* Form per aggiungere o modificare */}
      {(isAdding || editingId) && (
        <div className="form-card">
          <div className="form-header">
            <h3>{editingId ? 'Edit Rate' : 'Add New Rate'}</h3>
            <button className="btn-close" onClick={resetForm}>
              <X size={20} />
            </button>
          </div>
          <form onSubmit={editingId ? handleUpdate : handleAdd}>
            <div className="form-grid">
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1', marginTop: '4px', paddingTop: '4px' }}>
                <h4 style={{ margin: 0, color: '#0066CC' }}>EURIBOR Rates <span style={{ fontWeight: 'normal', fontSize: '0.85em', color: '#888' }}>(optional — leave blank if European holiday)</span></h4>
              </div>
              <div className="form-group">
                <label>Euribor 1 Week (%)</label>
                <input
                  type="number"
                  step="0.001"
                  name="euribor1W"
                  value={formData.euribor1W}
                  onChange={handleInputChange}
                  placeholder="e.g., 3.125"
                />
              </div>
              <div className="form-group">
                <label>Euribor 1 Month (%)</label>
                <input
                  type="number"
                  step="0.001"
                  name="euribor1M"
                  value={formData.euribor1M}
                  onChange={handleInputChange}
                  placeholder="e.g., 3.250"
                />
              </div>
              <div className="form-group">
                <label>Euribor 3 Months (%)</label>
                <input
                  type="number"
                  step="0.001"
                  name="euribor3M"
                  value={formData.euribor3M}
                  onChange={handleInputChange}
                  placeholder="e.g., 3.375"
                />
              </div>
              <div className="form-group">
                <label>Euribor 6 Months (%)</label>
                <input
                  type="number"
                  step="0.001"
                  name="euribor6M"
                  value={formData.euribor6M}
                  onChange={handleInputChange}
                  placeholder="e.g., 3.500"
                />
              </div>
              <div className="form-group">
                <label>Euribor 12 Months (%)</label>
                <input
                  type="number"
                  step="0.001"
                  name="euribor12M"
                  value={formData.euribor12M}
                  onChange={handleInputChange}
                  placeholder="e.g., 3.625"
                />
              </div>

              {/* Campi €STR */}
              <div className="form-group" style={{ gridColumn: '1 / -1', marginTop: '20px', paddingTop: '20px', borderTop: '2px solid #e0e0e0' }}>
                <h4 style={{ margin: 0, color: '#17A2B8' }}>€STR Rates (Optional)</h4>
              </div>
              <div className="form-group">
                <label>€STR O/N (%)</label>
                <input
                  type="number"
                  step="0.001"
                  name="estrON"
                  value={formData.estrON}
                  onChange={handleInputChange}
                  placeholder="e.g., 2.950"
                />
              </div>

              {/* Campi ON altre valute */}
              <div className="form-group" style={{ gridColumn: '1 / -1', marginTop: '20px', paddingTop: '20px', borderTop: '2px solid #e0e0e0' }}>
                <h4 style={{ margin: 0, color: '#E53935' }}>ON Rates — SONIA / SOFR / AONIA (Optional)</h4>
              </div>
              <div className="form-group">
                <label>SONIA O/N — GBP (%)</label>
                <input
                  type="number"
                  step="0.001"
                  name="soniaON"
                  value={formData.soniaON}
                  onChange={handleInputChange}
                  placeholder="e.g., 5.200"
                />
              </div>
              <div className="form-group">
                <label>SOFR O/N — USD (%)</label>
                <input
                  type="number"
                  step="0.001"
                  name="sofrON"
                  value={formData.sofrON}
                  onChange={handleInputChange}
                  placeholder="e.g., 5.300"
                />
              </div>
              <div className="form-group">
                <label>AONIA O/N — AUD (%)</label>
                <input
                  type="number"
                  step="0.001"
                  name="aoniaON"
                  value={formData.aoniaON}
                  onChange={handleInputChange}
                  placeholder="e.g., 4.350"
                />
              </div>

            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="btn-save">
                <Save size={18} />
                {editingId ? 'Update Rate' : 'Add Rate'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tabella dei tassi */}
      <div className="rates-table-card">
        <h3>Historical Rates</h3>
        <div className="table-wrapper">
          <table className="rates-table">
            <thead>
              <tr>
                <th>Date</th>
                <th colSpan="5" style={{ backgroundColor: '#f5f9ff', color: '#0066CC' }}>EURIBOR</th>
                <th colSpan="1" style={{ backgroundColor: '#e0f7fa', color: '#17A2B8' }}>€STR</th>
                <th colSpan="3" style={{ backgroundColor: '#fce4ec', color: '#E53935' }}>ON Rates</th>
                <th>Actions</th>
              </tr>
              <tr>
                <th></th>
                <th>1W</th>
                <th>1M</th>
                <th>3M</th>
                <th>6M</th>
                <th>12M</th>
                <th>O/N</th>
                <th style={{ color: '#E53935' }}>SONIA</th>
                <th style={{ color: '#1E88E5' }}>SOFR</th>
                <th style={{ color: '#43A047' }}>AONIA</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rates.map(rate => (
                <tr key={rate._id}>
                  <td className="date-cell">{formatDate(rate.date)}</td>
                  <td>{rate.euribor1W != null ? rate.euribor1W.toFixed(3) + '%' : '-'}</td>
                  <td>{rate.euribor1M != null ? rate.euribor1M.toFixed(3) + '%' : '-'}</td>
                  <td>{rate.euribor3M != null ? rate.euribor3M.toFixed(3) + '%' : '-'}</td>
                  <td>{rate.euribor6M != null ? rate.euribor6M.toFixed(3) + '%' : '-'}</td>
                  <td>{rate.euribor12M != null ? rate.euribor12M.toFixed(3) + '%' : '-'}</td>
                  <td>{rate.estrON != null ? rate.estrON.toFixed(3) + '%' : '-'}</td>
                  <td>{rate.soniaON != null ? rate.soniaON.toFixed(3) + '%' : '-'}</td>
                  <td>{rate.sofrON != null ? rate.sofrON.toFixed(3) + '%' : '-'}</td>
                  <td>{rate.aoniaON != null ? rate.aoniaON.toFixed(3) + '%' : '-'}</td>
                  <td className="actions-cell">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(rate)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(rate._id)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rates.length === 0 && (
            <div className="no-data">
              <p>No rates available. Add your first rate to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EuriborManagement;